import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useBooking } from "../BookingContext";
import { useCustomerAuth } from "../AuthContext";
import { publicCarsApi, publicLocationsApi, CONTACT } from "../../api";
import {
  bookings as bookingsApi,
  promo as promoApi,
  locations as damsLocationsApi,
} from "../damsApi";
import { DepositCard, MileagePolicyCard } from "../components/DepositPolicy";
import {
  ArrowLeft,
  Banknote,
  Shield,
  Loader2,
  MessageCircle,
  Mail,
  Phone,
  Tag,
  Fuel,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const {
    carId,
    pickupLocation,
    dropoffLocation,
    pickupDate,
    dropoffDate,
    days,
    payment,
    updatePayment,
  } = useBooking();
  const { user, loading: authLoading } = useCustomerAuth();

  const [processing, setProcessing] = useState(false);
  const [car, setCar] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationPolicy, setLocationPolicy] = useState(null); // {tax_rate, unlimited_mileage, mileage_limit_per_day, extra_mileage_charge, refuel_amount, ...}
  const [loading, setLoading] = useState(true);

  // Promo state
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // { discount_amount, ... }
  const [promoBusy, setPromoBusy] = useState(false);

  // Refuel opt-in
  const [refuelOptIn, setRefuelOptIn] = useState(false);

  // Force cash payment (Stripe card payment temporarily disabled)
  useEffect(() => {
    if (payment.method !== "cash") updatePayment({ method: "cash" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect to sign-in if not authenticated (single source of truth requires it)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/sign-in", { state: { from: "/checkout" } });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const carPromise = carId ? publicCarsApi.get(carId).catch(() => null) : Promise.resolve(null);
    Promise.all([carPromise, publicLocationsApi.list().catch(() => [])])
      .then(([c, l]) => {
        setCar(c);
        setLocations(l);
      })
      .finally(() => setLoading(false));
  }, [carId]);

  // Fetch full policy (incl. unlimited_mileage / mileage_limit_per_day) for the pickup location
  useEffect(() => {
    if (!pickupLocation) return;
    let cancelled = false;
    damsLocationsApi
      .taxByName(pickupLocation)
      .then((p) => {
        if (!cancelled) setLocationPolicy(p);
      })
      .catch(() => {
        if (!cancelled) setLocationPolicy(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pickupLocation]);

  if (loading || authLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-gray-400">
        <Loader2 className="animate-spin inline mr-2" size={18} /> Loading checkout...
      </div>
    );
  }
  if (!user) return null;
  if (!car) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold">No vehicle selected.</h2>
        <button className="pill-btn primary mt-4" onClick={() => navigate("/cars")}>
          {t("hero.ctaBrowse")}
        </button>
      </div>
    );
  }

  // Resolve full location object (the rental-routes API expects the whole object)
  const pickupLoc =
    locations.find((l) => l.name === pickupLocation) || locations[0] || null;
  const dropoffLoc =
    locations.find((l) => l.name === (dropoffLocation || pickupLocation)) || pickupLoc;

  const taxRate = Number(pickupLoc?.tax_rate ?? pickupLoc?.tax ?? 0);
  const refuelAmount = refuelOptIn ? Number(pickupLoc?.refuel_amount || 0) : 0;
  const pricePerDay = Number(car.price || car.price_per_day || 0);
  const subtotalBase = pricePerDay * days;
  const discount = Number(promoApplied?.discount_amount || 0);
  const subtotal = Math.max(0, subtotalBase - discount) + refuelAmount;
  const tax = +(subtotal * (taxRate / 100)).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoBusy(true);
    try {
      const res = await promoApi.validate({ code: promoCode.trim(), subtotal: subtotalBase });
      setPromoApplied(res);
      toast.success("Promo code applied");
    } catch (err) {
      setPromoApplied(null);
      toast.error("Invalid promo code", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setPromoBusy(false);
    }
  };

  const clearPromo = () => {
    setPromoCode("");
    setPromoApplied(null);
  };

  const onConfirm = async () => {
    if (!pickupLoc) {
      toast.error("Pick-up location unavailable");
      return;
    }
    setProcessing(true);
    try {
      const created = await bookingsApi.create({
        car_id: car.id,
        pickup_date: pickupDate,
        dropoff_date: dropoffDate,
        pickup_location: pickupLoc,
        dropoff_location: dropoffLoc,
        payment_method: "cash",
        promo_code: promoApplied ? promoCode.trim() : undefined,
        refuel_opted_in: refuelOptIn,
        terms_accepted: true,
      });

      // Persist a minimal snapshot for the confirmation page.
      const snapshot = {
        booking_id: created.id,
        car_name: created.car_name,
        car_image: car.image,
        pickup_date: created.pickup_date,
        dropoff_date: created.dropoff_date,
        days: created.days,
        pickup_location_name: pickupLoc?.name,
        pickup_location: pickupLoc,
        subtotal: created.subtotal,
        discount_amount: created.discount_amount,
        refuel_amount: created.refuel_amount,
        tax_amount: created.tax_amount,
        tax_rate: created.tax_rate,
        total_price: created.total_price,
        deposit: created.deposit,
        extra_mileage_fee: created.extra_mileage_fee,
        extra_mileage_km: created.extra_mileage_km,
        extra_mileage_rate: created.extra_mileage_rate,
        odometer_in: created.odometer_in,
        odometer_out: created.odometer_out,
        payment_method: created.payment_method,
        status: created.status,
        user_email: created.user_email,
        user_name: created.user_name,
      };
      localStorage.setItem("dams_last_reservation", JSON.stringify(snapshot));
      navigate("/confirmation");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error("Booking failed", { description: msg });
      setProcessing(false);
    }
  };

  const refuelLabel = pickupLoc?.refuel_amount > 0
    ? `Pre-paid refuel (+$${Number(pickupLoc.refuel_amount).toFixed(2)})`
    : "Pre-paid refuel";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 mb-5"
      >
        <ArrowLeft size={14} /> {t("checkout.back")}
      </button>
      <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2" data-testid="checkout-title">
        {t("checkout.title")}
      </h1>
      <p className="text-gray-500 mb-6">
        Signed in as <span className="font-semibold text-gray-700">{user.email}</span>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="card-soft p-6 space-y-6">
          {/* Renter card */}
          <section>
            <h2 className="text-lg font-bold mb-3">Renter</h2>
            <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 text-sm flex items-center gap-3">
              <CheckCircle2 className="text-emerald-500 shrink-0" size={18} />
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{user.name}</p>
                <p className="text-gray-500 text-xs">{user.email}{user.phone ? ` · ${user.phone}` : ""}</p>
              </div>
            </div>
          </section>

          {/* Extras */}
          {pickupLoc?.refuel_amount > 0 && (
            <section>
              <h2 className="text-lg font-bold mb-3 inline-flex items-center gap-2">
                <Fuel size={18} className="text-red-500" /> Extras
              </h2>
              <label className="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors hover:border-gray-300"
                style={{ borderColor: refuelOptIn ? "#ef4444" : "#e5e7eb", background: refuelOptIn ? "#fef2f2" : "#fff" }}>
                <input
                  type="checkbox"
                  checked={refuelOptIn}
                  onChange={(e) => setRefuelOptIn(e.target.checked)}
                  className="mt-1"
                  data-testid="refuel-optin"
                />
                <div>
                  <div className="font-bold text-sm">{refuelLabel}</div>
                  <p className="text-xs text-gray-500">Return the car at any fuel level — we'll handle the refuel.</p>
                </div>
              </label>
            </section>
          )}

          {/* Promo */}
          <section>
            <h2 className="text-lg font-bold mb-3 inline-flex items-center gap-2">
              <Tag size={18} className="text-red-500" /> Promo code
            </h2>
            {promoApplied ? (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm flex items-center justify-between">
                <span className="text-emerald-900">
                  <strong>{promoCode.toUpperCase()}</strong> applied — −${discount.toFixed(2)}
                </span>
                <button onClick={clearPromo} className="text-emerald-700 hover:text-emerald-900 text-xs underline">
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="input-soft flex-1"
                  placeholder="Enter code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  data-testid="promo-input"
                />
                <button
                  onClick={validatePromo}
                  disabled={promoBusy || !promoCode.trim()}
                  className="pill-btn outline"
                  style={{ padding: "0.5rem 1rem" }}
                  data-testid="promo-apply"
                >
                  {promoBusy ? "..." : "Apply"}
                </button>
              </div>
            )}
          </section>

          {/* Payment — Stripe temporarily disabled. Cash at pick-up only. */}
          <section>
            <h2 className="text-lg font-bold mb-3">{t("checkout.paymentMethod")}</h2>
            <div
              className="text-left p-5 rounded-xl border-2"
              style={{ borderColor: "#ef4444", background: "#fef2f2" }}
              data-testid="pay-cash"
            >
              <Banknote className="text-red-600" />
              <div className="mt-2 font-bold">{t("checkout.cash")}</div>
              <p className="text-xs text-gray-500">{t("checkout.cashNotice")}</p>
            </div>
          </section>

          <div className="flex justify-end pt-3">
            <button
              onClick={onConfirm}
              disabled={processing}
              className="pill-btn primary"
              style={{ padding: "0.85rem 1.6rem" }}
              data-testid="confirm-booking"
            >
              {processing ? (
                <>
                  <Loader2 className="animate-spin inline mr-1" size={14} /> {t("checkout.processing")}
                </>
              ) : (
                t("checkout.confirm")
              )}
            </button>
          </div>
        </div>

        {/* Trip summary */}
        <aside>
          <div className="card-soft p-5 sticky top-24" data-testid="trip-summary">
            <h3 className="font-bold mb-4">{t("checkout.tripSummary")}</h3>
            <div className="flex gap-3 mb-4">
              {car.image && <img src={car.image} alt="" className="w-20 h-16 rounded-lg object-cover" />}
              <div>
                <p className="font-bold leading-tight">{car.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {car.category} · {car.seats} seats
                </p>
              </div>
            </div>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("detail.pickup")}</span>
                <span className="font-semibold text-right">{pickupDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("detail.dropoff")}</span>
                <span className="font-semibold text-right">{dropoffDate}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("detail.days")}</span>
                <span className="font-semibold">{days}</span>
              </div>
            </div>

            {/* Deposit + Mileage policy (rendered between trip details and cost breakdown) */}
            <DepositCard amount={car.deposit} variant="light" />
            <MileagePolicyCard car={car} policy={locationPolicy} days={days} />

            <div className="text-sm space-y-2 mt-3">
              <div className="subtle-divider" />
              <div className="flex justify-between">
                <span className="text-gray-600">{t("detail.subtotal")}</span>
                <span>${subtotalBase.toFixed(2)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Promo discount</span>
                  <span>−${discount.toFixed(2)}</span>
                </div>
              )}
              {refuelAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Refuel</span>
                  <span>+${refuelAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t("detail.tax")} ({taxRate}%)
                </span>
                <span>${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-base pt-2">
                <span className="font-bold">{t("detail.total")}</span>
                <span className="font-extrabold text-red-600 text-xl" data-testid="checkout-total">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-4">
              <Shield size={12} /> Free cancellation up to 48h before pick-up
            </p>

            <div className="subtle-divider mt-4" />
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Need help?</p>
            <div className="grid grid-cols-3 gap-2">
              <a
                href={`${CONTACT.whatsappLink}?text=${encodeURIComponent(
                  `Hi! I'm booking the ${car.name} from ${pickupDate} to ${dropoffDate}.`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pill-btn outline inline-flex items-center justify-center gap-1.5"
                style={{ padding: "0.5rem 0.4rem", fontSize: "0.75rem" }}
              >
                <MessageCircle size={13} className="text-emerald-600" /> Chat
              </a>
              <a
                href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(`Booking — ${car.name}`)}`}
                className="pill-btn outline inline-flex items-center justify-center gap-1.5"
                style={{ padding: "0.5rem 0.4rem", fontSize: "0.75rem" }}
              >
                <Mail size={13} className="text-red-500" /> Email
              </a>
              <a
                href={CONTACT.phoneLink}
                className="pill-btn outline inline-flex items-center justify-center gap-1.5"
                style={{ padding: "0.5rem 0.4rem", fontSize: "0.75rem" }}
              >
                <Phone size={13} className="text-blue-600" /> Call
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
